// ── Event hooks → StatsStore ─────────────────────────────────────────────────

import type { ExtensionContext } from '@earendil-works/pi-coding-agent';
import { StatsStore } from './store.js';
import type { UsageSnapshot, ToolRecord, CompactionRecord, ErrorRecord, TurnRecord } from './types.js';
import { formatFooterLine } from './format.js';

// Tool timing tracker: start → end
const toolStartTimes = new Map<string, number>();

export class StatsCollector {
  private store: StatsStore;
  private updateFooter: (ctx: ExtensionContext) => void;
  private lastFooterUpdate = 0;
  private pendingFooterUpdate = false;
  private ctx: ExtensionContext | null = null;

  constructor(store: StatsStore, updateFooter: (ctx: ExtensionContext) => void) {
    this.store = store;
    this.updateFooter = updateFooter;
  }

  /** Cleanup: cancel pending footer timer */
  destroy(): void {
    this.ctx = null;
    this.pendingFooterUpdate = false;
  }

  setContext(ctx: ExtensionContext): void {
    this.ctx = ctx;
  }

  // ── Debounced footer refresh ──────────────────────────────────────────────

  private debouncedRefresh(): void {
    if (!this.ctx) return;
    const now = Date.now();
    if (now - this.lastFooterUpdate > 500) {
      this.lastFooterUpdate = now;
      this.updateFooter(this.ctx);
    } else if (!this.pendingFooterUpdate) {
      this.pendingFooterUpdate = true;
      setTimeout(() => {
        this.pendingFooterUpdate = false;
        if (this.ctx) {
          this.lastFooterUpdate = Date.now();
          this.updateFooter(this.ctx);
        }
      }, 500);
    }
  }

  // ── message_end: extract usage ────────────────────────────────────────────

  onMessageEnd(event: any): void {
    const msg = event.message;
    if (msg?.role !== 'assistant' || !msg.usage) return;

    const usage = msg.usage;
    this.store.appendUsage({
      ts: Date.now(),
      provider: msg.provider ?? 'unknown',
      model: msg.model ?? 'unknown',
      input: usage.input ?? 0,
      output: usage.output ?? 0,
      cacheRead: usage.cacheRead ?? 0,
      cacheWrite: usage.cacheWrite ?? 0,
      costTotal: usage.cost?.total ?? 0,
      costInput: usage.cost?.input ?? 0,
      costOutput: usage.cost?.output ?? 0,
      costCacheRead: usage.cost?.cacheRead ?? 0,
      costCacheWrite: usage.cost?.cacheWrite ?? 0,
    });

    this.debouncedRefresh();
  }

  // ── tool_execution_start / end ────────────────────────────────────────────

  onToolExecutionStart(event: any): void {
    toolStartTimes.set(event.toolCallId, Date.now());
  }

  onToolExecutionEnd(event: any): void {
    const startTime = toolStartTimes.get(event.toolCallId);
    const durationMs = startTime ? Date.now() - startTime : 0;
    toolStartTimes.delete(event.toolCallId);

    // Estimate result size
    let resultSize = 0;
    if (event.result) {
      try { resultSize = JSON.stringify(event.result).length; } catch { resultSize = 0; }
    }

    this.store.appendTool({
      ts: Date.now(),
      toolName: event.toolName ?? 'unknown',
      durationMs,
      isError: event.isError ?? false,
      resultSize,
    });

    this.debouncedRefresh();
  }

  // ── session_before_compact ─────────────────────────────────────────────────

  onSessionBeforeCompact(event: any, ctx: ExtensionContext): void {
    const contextUsage = ctx.getContextUsage();
    this.store.appendCompaction({
      ts: Date.now(),
      beforeTokens: contextUsage?.tokens ?? null,
      afterTokens: null, // Will be updated on next message_end
    });
  }

  // ── after_provider_response ────────────────────────────────────────────────

  onAfterProviderResponse(event: any): void {
    const status = event.status ?? 0;
    if (status >= 400) {
      this.store.appendError({
        ts: Date.now(),
        status,
        provider: 'unknown', // Not available in this event
      });
    }
  }

  // ── turn_end ───────────────────────────────────────────────────────────────

  onTurnEnd(event: any): void {
    const toolCallCount = event.toolResults?.length ?? 0;
    this.store.appendTurn({
      ts: Date.now(),
      turnIndex: event.turnIndex ?? 0,
      toolCallCount,
    });

    this.debouncedRefresh();
  }

  // ── session_start ──────────────────────────────────────────────────────────

  onSessionStart(event: any, ctx: ExtensionContext): void {
    this.setContext(ctx);
    // Refresh footer to show initial state
    this.debouncedRefresh();
  }

  // ── Build footer data ─────────────────────────────────────────────────────

  getFooterData(ctx: ExtensionContext): string {
    const cacheSummary = this.store.getCacheSummary();
    const toolSummary = this.store.getToolSummary();
    const stats = this.store.getStats();

    return formatFooterLine({
      cacheHitRate: cacheSummary.hitRate,
      toolCallCount: toolSummary.totalCalls,
      avgToolDurationMs: toolSummary.avgDurationMs,
      errorCount: stats.errorRecords.length,
    });
  }
}